import React, { useEffect, useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { object, string } from "yup";

import { ErrorMessage } from "basics/styles";
import Modal from "components/Modal";
import CloseIcon from "icons/CloseIcon";

import {
  Card,
  CloseButton,
  Heading,
  Title,
  Subtitle,
  Form,
  Field,
  Label,
  Input,
  SubmitButton,
  StatusContainer,
  StatusIcon,
  StatusTitle,
  StatusText,
  Spinner,
} from "./styles";

import useAuth from "hooks/useAuth";

const validations = {
  userName: string()
    .email("Ingresá un mail con el formato correcto")
    .required("El mail es obligatorio"),
};

/* El login es por link al mail (sin contraseña). El cuadro se apoya en
   `components/Modal`, que trae el fondo oscurecido, el cierre con Esc o click
   afuera, el bloqueo del scroll y el `inert` sobre el resto de la página.

   El estado "te enviamos el link" es local (`sentTo`) y queda abierto hasta
   que el usuario lo cierre. `sendLinkMail` valida el mail y manda el link en
   una sola llamada a la Cloud Function, así que `loading` cubre las dos cosas. */
const LoginForm = ({ onClose, open }) => {
  const { loading, sendLinkMail } = useAuth();
  const [sentTo, setSentTo] = useState();
  const [messageError, setMessageError] = useState();
  const inputRef = useRef(null);

  const { register, handleSubmit, errors, reset } = useForm({
    resolver: yupResolver(object(validations)),
  });

  useEffect(() => {
    if (!open) return;

    setSentTo();
    setMessageError();
    reset();

    /* `Modal` hace blur del elemento activo al abrir y muestra el contenido
       recién después de su transición: el foco va después de eso. */
    const timer = window.setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 450);

    return () => window.clearTimeout(timer);
  }, [open, reset]);

  const onSubmit = (data) => {
    setMessageError();

    sendLinkMail(data.userName)
      .then(() => setSentTo(data.userName))
      .catch((error) => setMessageError(error));
  };

  const registerInput = (element) => {
    register(element);
    inputRef.current = element;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <StatusContainer>
          <Spinner />
          <StatusTitle as="p">Enviando el link…</StatusTitle>
          <StatusText>
            Estamos verificando el mail y enviando el link de acceso.
          </StatusText>
        </StatusContainer>
      );
    }

    if (sentTo) {
      return (
        <>
          <StatusContainer>
            <StatusIcon>
              <svg viewBox="0 0 24 24">
                <path d="M4 6h16v12H4z" />
                <path d="m4 7 8 6 8-6" />
              </svg>
            </StatusIcon>
            <StatusTitle>Te enviamos el link de acceso</StatusTitle>
            <StatusText>
              Revisá la casilla de <strong>{sentTo}</strong> y hacé click en el
              link para ingresar. Si no lo ves, mirá en la carpeta de spam.
            </StatusText>
          </StatusContainer>
          <SubmitButton type="button" onClick={onClose}>
            Listo
          </SubmitButton>
        </>
      );
    }

    return (
      <>
        <Heading>
          <Title>Ingresar</Title>
          <Subtitle>
            Ingresá el mail con el que estás registrado. Te enviamos un link de
            acceso a tu casilla, sin contraseña.
          </Subtitle>
        </Heading>

        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Field>
            <Label htmlFor="userName">Email</Label>
            <Input
              id="userName"
              name="userName"
              type="email"
              placeholder="nombre@empresa.com"
              autoComplete="email"
              aria-invalid={errors.userName ? "true" : "false"}
              ref={registerInput}
            />
            {errors.userName && (
              <ErrorMessage role="alert">{errors.userName.message}</ErrorMessage>
            )}
            {messageError && (
              <ErrorMessage role="alert">{messageError}</ErrorMessage>
            )}
          </Field>

          <SubmitButton type="submit">Enviar link de acceso</SubmitButton>
        </Form>
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose} locked={loading}>
      <Card>
        <CloseButton type="button" aria-label="Cerrar" onClick={onClose}>
          <CloseIcon width={14} height={14} fill="currentColor" />
        </CloseButton>
        {renderContent()}
      </Card>
    </Modal>
  );
};

export default LoginForm;
